import type { ReactNode } from 'react'
import { roleAtLeast, roleSchema, type Role } from '@lms/shared-types'
import { useSession } from '../../auth/auth-context'
import { Card } from '../primitives/Card'
import { useEffectiveEmployeeState, useEffectiveRole } from '../shell/effective-employee'

/**
 * HR pages should be inaccessible while a privileged user is "Viewing As" an
 * employee — the page chrome would lie about whose data is on screen and the
 * inline edit affordances make no sense in preview mode. Instead of letting the
 * page mount and hand-rolling per-page banners, every HR page wraps its body
 * in this guard.
 *
 * Renders the page (`children`) when:
 *   - real role is HR or higher AND
 *   - effective role is HR or higher (i.e. not impersonating a non-HR user).
 *
 * Renders a paused-notice Card with an "Exit Viewing As" button when:
 *   - real role is HR or higher AND
 *   - effective role drops below HR (because Viewing As is active on a
 *     non-HR employee).
 *
 * If the real role isn't HR-or-higher we still render `children` — route-level
 * `RequireRole` is the source of truth for hard access control, this guard is
 * specifically about the impersonation conflict.
 */

interface HrAccessGuardProps {
  children: ReactNode
}

export function HrAccessGuard({ children }: HrAccessGuardProps): JSX.Element {
  const { user } = useSession()
  const parsedRealRole = user ? roleSchema.safeParse(user.role) : null
  const realRole: Role = parsedRealRole?.success ? parsedRealRole.data : 'employee'
  const effectiveRole = useEffectiveRole(realRole)
  const { clear } = useEffectiveEmployeeState()

  const isHrAtLeast = roleAtLeast(realRole, 'hr')
  const effectiveIsHrAtLeast = roleAtLeast(effectiveRole, 'hr')

  // Real role isn't HR+ → nothing to guard. Route-level RequireRole already
  // blocks unauthorised users; we just pass through.
  if (!isHrAtLeast) {
    return <>{children}</>
  }

  // Real role is HR+ but the impersonation has dropped the effective role
  // below HR — pause the HR surface and offer an exit.
  if (!effectiveIsHrAtLeast) {
    return (
      <Card
        padding={24}
        style={{ maxWidth: 640 }}
      >
        <div
          data-testid="hr-access-guard-paused"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-3)',
              fontWeight: 600,
            }}
          >
            Viewing As active
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 20,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: 'var(--text)',
            }}
          >
            HR tools paused while previewing another employee
          </h2>
          <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>
            You're previewing the app as a non-HR employee. To open HR tools again,
            exit Viewing As and return to your own session.
          </p>
          <div>
            <button
              type="button"
              data-testid="hr-access-guard-exit"
              onClick={clear}
              style={{
                marginTop: 4,
                padding: '8px 14px',
                borderRadius: 10,
                border: '1px solid var(--line-strong)',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Exit Viewing As
            </button>
          </div>
        </div>
      </Card>
    )
  }

  return <>{children}</>
}
